import React from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import FeatherIcon from "feather-icons-react";
import { Alert, Button } from "reactstrap";

import { ToastHelper } from "../../helpers/alerts/alertHelper";
import { etiquetaPerfil, etiquetaTipo } from "./tipos";

/**
 * Detalle de un cambio, en el orden en que lo necesita soporte durante una
 * llamada: que es, a quien le aplica, donde se ve, que tiene que hacer el
 * cliente y que responder a las dudas tipicas.
 */
const NovedadDetalle = ({ cambio, novedad }) => {
  const tipo = etiquetaTipo(cambio.tipo);
  const faq = cambio.faq ?? [];

  /** Version en texto plano, para pegar en el chat o el correo del cliente. */
  const texto = () =>
    [
      cambio.titulo,
      `Dónde se ve: ${cambio.donde ?? "-"}`,
      `Perfiles: ${(cambio.perfiles ?? []).join(", ") || "-"}`,
      "",
      cambio.descripcion ?? "",
      "",
      cambio.accion
        ? `Acción requerida: ${cambio.accion}`
        : "No requiere acción del cliente.",
      ...(faq.length
        ? ["", "Preguntas frecuentes:"].concat(
            faq.map((f) => `- ${f.pregunta}\n  ${f.respuesta}`)
          )
        : []),
      "",
      `Publicado el ${dayjs(novedad.fecha).format("DD MMM YYYY")} (${
        novedad.version ?? "-"
      })`,
    ].join("\n");

  const copiar = () => {
    try {
      navigator.clipboard.writeText(texto());
      ToastHelper("Novedad copiada al portapapeles", "success");
    } catch (e) {
      // navigator.clipboard no existe fuera de contextos seguros (http).
      ToastHelper("No se pudo copiar la novedad", "error");
    }
  };

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between">
        <div>
          <h5 className="mb-2">{cambio.titulo}</h5>
          <span className={`badge bg-${tipo.color} me-1`}>
            <FeatherIcon icon={tipo.icon} className="icon-xs me-1" />
            {tipo.label}
          </span>
          {(cambio.perfiles ?? []).map((perfil) => {
            const p = etiquetaPerfil(perfil);
            return (
              <span key={perfil} className={`badge bg-${p.color} me-1`}>
                {p.label}
              </span>
            );
          })}
        </div>
        <Button
          color="light"
          size="sm"
          className="text-nowrap ms-2"
          onClick={copiar}
        >
          <FeatherIcon icon="copy" className="icon-xs me-1" />
          Copiar
        </Button>
      </div>

      {cambio.donde && (
        <p className="text-primary mt-3 mb-2">
          <FeatherIcon icon="map-pin" className="icon-xs me-1" />
          {cambio.donde}
        </p>
      )}

      <p className="mb-3">{cambio.descripcion}</p>

      {cambio.accion ? (
        <Alert color="warning" className="d-flex align-items-start">
          <FeatherIcon icon="alert-circle" className="icon-sm me-2" />
          <span>
            <strong className="d-block">Acción requerida</strong>
            {cambio.accion}
          </span>
        </Alert>
      ) : (
        <p className="text-muted font-size-12">
          <FeatherIcon icon="check-circle" className="icon-xs me-1" />
          No requiere acción del cliente.
        </p>
      )}

      {faq.length > 0 && (
        <div className="mt-4">
          <h6 className="text-uppercase font-size-12 text-muted mb-3">
            Preguntas frecuentes
          </h6>
          {faq.map((item, idx) => (
            <div key={idx} className="mb-3">
              <p className="mb-1 fw-bold">{item.pregunta}</p>
              <p className="text-muted mb-0">{item.respuesta}</p>
            </div>
          ))}
        </div>
      )}

      <p className="text-muted font-size-12 mt-4 mb-0">
        Publicado el {dayjs(novedad.fecha).format("DD MMM YYYY")}
        {novedad.version ? ` · ${novedad.version}` : ""}
      </p>
    </div>
  );
};

NovedadDetalle.propTypes = {
  cambio: PropTypes.object.isRequired,
  novedad: PropTypes.object.isRequired,
};

export default NovedadDetalle;
